import { addDays, fmtDate, fmtMonth, fmtMonthShort } from './format'

/**
 * Date-bucketing for the Trends charts: group day/month rows into day / week /
 * month / quarter / year buckets, format bucket keys, and map a bucket back to
 * the inclusive date range it covers. Pure date logic — lived inside the
 * Trends page component before.
 */

// Ordered finest → coarsest; the chart picks the finest that fits the width.
export type Bucket = 'day' | 'week' | 'month' | 'quarter' | 'year'
export const BUCKET_ORDER: Bucket[] = ['day', 'week', 'month', 'quarter', 'year']

/** Sunday (YYYY-MM-DD) of the week containing `dayKey`. */
export function weekStart(dayKey: string): string {
  const [y, m, d] = dayKey.split('-').map(Number)
  return addDays(dayKey, -new Date(y, m - 1, d).getDay())
}

export function bucketOf(key: string, bucket: Bucket): string {
  if (bucket === 'day') return key
  if (bucket === 'week') return weekStart(key)
  if (bucket === 'month') return key.slice(0, 7)
  const [y, m] = key.split('-').map(Number)
  if (bucket === 'year') return String(y)
  return `${y} Q${Math.floor((m - 1) / 3) + 1}`
}

/** "Jul 14" for a YYYY-MM-DD day key. */
function fmtDayShort(key: string): string {
  return fmtDate(key).replace(/, \d{4}$/, '')
}

export function fmtBucket(key: string, bucket: Bucket): string {
  if (bucket === 'day') return fmtDate(key)
  if (bucket === 'week') return `Week of ${fmtDate(key)}`
  return bucket === 'month' ? fmtMonth(key) : key
}

export function fmtBucketShort(key: string, bucket: Bucket): string {
  if (bucket === 'day' || bucket === 'week') return fmtDayShort(key)
  return bucket === 'month' ? fmtMonthShort(key) : key
}

/** The inclusive [from, to] date range a bucket key covers, for filtering the
 * transaction ledger. */
export function bucketRange(key: string, bucket: Bucket): [string, string] {
  if (bucket === 'day') return [key, key]
  if (bucket === 'week') return [key, addDays(key, 6)]
  if (bucket === 'month') return [`${key}-01`, `${key}-31`]
  if (bucket === 'year') return [`${key}-01-01`, `${key}-12-31`]
  const q = key.match(/^(\d{4}) Q(\d)$/) // "2026 Q2"
  if (q) {
    const startMo = (Number(q[2]) - 1) * 3 + 1
    return [`${q[1]}-${String(startMo).padStart(2, '0')}-01`, `${q[1]}-${String(startMo + 2).padStart(2, '0')}-31`]
  }
  return [key, key]
}

/** Progressively lighter shades of a base color for sub-category slices
 * (rank 0 = full color). Keeps a drilled category visually tied to its parent. */
export function shadeOf(base: string, i: number): string {
  const pct = Math.max(38, 100 - i * 12)
  return `color-mix(in srgb, ${base} ${pct}%, white)`
}
